import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Typography, IconButton } from '@totalsoft/rocket-ui';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const AutomaticUserAllocationComponent = ({
  automaticUserAllocationsOfDealer,
  isDirty,
  cardIndex,
  validation,
  onLoadDealers,
  selectedDealerIds,
  userOptions,
  responsibleOptions,
  salesResponsibleOptions,
  asmResponsibleOptions,
  insuranceResponsibleOptions,
  onPropertyChange,
  onDeleteCard,
  onAddAllocation,
  onDeleteAllocation,
  onSave
}) => {
  const { t } = useTranslation();
  const automaticUserAllocation = automaticUserAllocationsOfDealer[0];
  const dealerUserOptions = useMemo(
    () =>
      userOptions
        .filter(x => x.partnerId === automaticUserAllocation.dealerId) //get only dealer users
        .map(y => ({ ...responsibleOptions.find(z => z.responsibleId === y.personId), ...y })) //join dealer users with persons
        .filter(a => a.name !== undefined), //remove users without corresponding person
    [automaticUserAllocation.dealerId, responsibleOptions, userOptions]
  );

  const selectedDealer = useMemo(
    () =>
      automaticUserAllocation.dealerId ? { dealerId: automaticUserAllocation.dealerId, name: automaticUserAllocation.dealerName } : null,
    [automaticUserAllocation.dealerId, automaticUserAllocation.dealerName]
  );

  return (
    <Grid container spacing={3}>
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <Typography color='error'>{validation |> getErrors}</Typography>
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            value={selectedDealer}
            loadOptions={onLoadDealers(selectedDealer, selectedDealerIds)}
            label={t('AutomaticUserAllocation.Dealer')}
            valueKey='dealerId'
            labelKey='name'
            onChange={event => onPropertyChange('dealerId', event.dealerId, cardIndex, 0, 'dealerName', event.name)}
            required
            error={!isValid(validation[cardIndex].allocations[0].dealerId)}
            helperText={getErrors(validation[cardIndex].allocations[0].dealerId)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={automaticUserAllocation.allUsers}
                onChange={event => onPropertyChange('allUsers', event.target.checked, cardIndex, 0)}
              />
            }
            label={t('AutomaticUserAllocation.AllUsers')}
            labelPlacement='end'
          />
        </Grid>
        <Grid container xs={6} sm={3} item justifyContent='flex-end'>
          <IconButton
            type='save'
            fontSize='medium'
            color='transparent'
            tooltip={t('Tooltips.Save')}
            onClick={onSave(cardIndex)}
            disabled={!isDirty || !(validation[cardIndex] |> isValid) || (validation |> getErrors) !== ''}
          />
          <IconButton
            type='delete'
            fontSize='medium'
            color='transparent'
            tooltip={t('Tooltips.Delete')}
            onClick={onDeleteCard(cardIndex)}
          />
        </Grid>
      </Grid>
      {automaticUserAllocation.allUsers ? (
        <>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              value={
                automaticUserAllocation.offerResponsibleId
                  ? { id: automaticUserAllocation.offerResponsibleId, name: automaticUserAllocation.offerResponsibleName }
                  : null
              }
              options={salesResponsibleOptions}
              label={t('AutomaticUserAllocation.OfferResponsible')}
              valueKey='id'
              labelKey='name'
              onChange={event => onPropertyChange('offerResponsibleId', event.id, cardIndex, 0, 'offerResponsibleName', event.name)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              value={
                automaticUserAllocation.offerAccountManagerId
                  ? { id: automaticUserAllocation.offerAccountManagerId, name: automaticUserAllocation.offerAccountManagerName }
                  : null
              }
              options={asmResponsibleOptions}
              label={t('AutomaticUserAllocation.OfferAccountManager')}
              valueKey='id'
              labelKey='name'
              onChange={event => onPropertyChange('offerAccountManagerId', event.id, cardIndex, 0, 'offerAccountManagerName', event.name)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              value={
                automaticUserAllocation.offerInsuranceUserId
                  ? { id: automaticUserAllocation.offerInsuranceUserId, name: automaticUserAllocation.offerInsuranceUserName }
                  : null
              }
              options={insuranceResponsibleOptions}
              label={t('AutomaticUserAllocation.OfferInsuranceUser')}
              valueKey='id'
              labelKey='name'
              onChange={event => onPropertyChange('offerInsuranceUserId', event.id, cardIndex, 0, 'offerInsuranceUserName', event.name)}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item>
            <IconButton type='add' fontSize='medium' color='transparent' tooltip={t('Add')} onClick={onAddAllocation(cardIndex)} />
          </Grid>
          <Grid item xs={11} container spacing={1}>
            {automaticUserAllocationsOfDealer.map((item, allocationIndex) => (
              <React.Fragment key={`${item.userId}-${allocationIndex}`}>
                <Grid container item xs={11} spacing={3}>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      value={{ userId: item.userId, name: item.userName }}
                      options={dealerUserOptions}
                      label={t('AutomaticUserAllocation.User')}
                      valueKey='userId'
                      labelKey='name'
                      required
                      onChange={event => onPropertyChange('userId', event.userId, cardIndex, allocationIndex, 'userName', event.name)}
                      disabled={!automaticUserAllocation.dealerId}
                      error={!isValid(validation[cardIndex].allocations[allocationIndex].userId)}
                      helperText={getErrors(validation[cardIndex].allocations[allocationIndex].userId)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      value={item.offerResponsibleId ? { id: item.offerResponsibleId, name: item.offerResponsibleName } : null}
                      options={salesResponsibleOptions}
                      label={t('AutomaticUserAllocation.OfferResponsible')}
                      valueKey='id'
                      labelKey='name'
                      onChange={event =>
                        onPropertyChange('offerResponsibleId', event.id, cardIndex, allocationIndex, 'offerResponsibleName', event.name)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      value={item.offerAccountManagerId ? { id: item.offerAccountManagerId, name: item.offerAccountManagerName } : null}
                      options={asmResponsibleOptions}
                      label={t('AutomaticUserAllocation.OfferAccountManager')}
                      valueKey='id'
                      labelKey='name'
                      onChange={event =>
                        onPropertyChange(
                          'offerAccountManagerId',
                          event.id,
                          cardIndex,
                          allocationIndex,
                          'offerAccountManagerName',
                          event.name
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      fullWidth
                      value={item.offerInsuranceUserId ? { id: item.offerInsuranceUserId, name: item.offerInsuranceUserName } : null}
                      options={insuranceResponsibleOptions}
                      label={t('AutomaticUserAllocation.OfferInsuranceUser')}
                      valueKey='id'
                      labelKey='name'
                      onChange={event =>
                        onPropertyChange('offerInsuranceUserId', event.id, cardIndex, allocationIndex, 'offerInsuranceUserName', event.name)
                      }
                    />
                  </Grid>
                </Grid>
                {automaticUserAllocationsOfDealer.length > 1 && (
                  <Grid item xs={1}>
                    <IconButton
                      type='delete'
                      fontSize='medium'
                      color='transparent'
                      tooltip={t('Tooltips.Delete')}
                      onClick={onDeleteAllocation(cardIndex, allocationIndex)}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};

AutomaticUserAllocationComponent.propTypes = {
  automaticUserAllocationsOfDealer: PropTypes.array.isRequired,
  isDirty: PropTypes.bool.isRequired,
  cardIndex: PropTypes.number.isRequired,
  validation: PropTypes.object.isRequired,
  onLoadDealers: PropTypes.func.isRequired,
  selectedDealerIds: PropTypes.array.isRequired,
  userOptions: PropTypes.array.isRequired,
  responsibleOptions: PropTypes.array.isRequired,
  salesResponsibleOptions: PropTypes.array.isRequired,
  asmResponsibleOptions: PropTypes.array.isRequired,
  insuranceResponsibleOptions: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onDeleteCard: PropTypes.func.isRequired,
  onAddAllocation: PropTypes.func.isRequired,
  onDeleteAllocation: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default AutomaticUserAllocationComponent;
